import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { defaultConfig } from "tailwind-variants";
import { parsePhoneNumberWithError } from "libphonenumber-js";

const twMergeConfig = {
  extend: {
    theme: {
      colors: ["primary", "secondary", "neutral"],
      spacing: ["18"],
      zIndex: ["60"],
    },
    classGroups: {
      "font-family": [
        {
          text: [
            "inter",
            "roboto",
            "montserrat",
            "poppins",
            "heading",
            "subheading",
            "body",
          ],
        },
      ],
      "font-size": [
        {
          text: [
            "heading-1",
            "heading-2",
            "heading-3",
            "heading-4",
            "subheading-1",
            "subheading-2",
            "subheading-3-400",
            "subheading-3-500",
            "body-1-300",
            "body-1-400",
            "body-2-300",
            "body-2-400",
            "body-3-300",
            "body-3-400",
            "button-1",
            "button-2",
            "button-3",
          ],
        },
      ],
    },
  },
};

defaultConfig.twMergeConfig = twMergeConfig;

const customTwMerge = extendTailwindMerge(twMergeConfig);

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs));
}

/**
 * This is used to verify if the current Website is running on a Development Environment
 */
export const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

/**
 * This is used for telling Next.js if the Website is deployed on Vercel
 *
 * Can be used for conditionally enabling features that we know are Vercel only
 *
 * @see https://vercel.com/docs/concepts/projects/environment-variables/system-environment-variables#framework-environment-variables
 */
export const VERCEL_ENV = process.env.NEXT_PUBLIC_VERCEL_ENV || undefined;

export const withUrl = (path: string) => {
  // Configure base URL when in a Vercel environment
  const vercelUrl = VERCEL_ENV
    ? `https://${process.env.NEXT_PUBLIC_VERCEL_URL}/`
    : undefined;

  // Override base URL when NEXT_PUBLIC_URL is set
  const baseUrl = process.env.NEXT_PUBLIC_URL ?? vercelUrl;

  // If base URL is defined, set base URL
  if (baseUrl) return new URL(path, baseUrl).href;
  // Fall back gracefully and just return the path
  else return path;
};

export const getPathNameOnly = (url: string) => {
  return new URL(url, "http://_").pathname;
};

export const CALLRAIL_PHONENUMBER = parsePhoneNumberWithError(
  process.env.NEXT_PUBLIC_CALLRAIL_SWAP_TARGET ?? "6503281001",
  "US"
);
export const CALLRAIL_HREF = CALLRAIL_PHONENUMBER.getURI();
export const CALLRAIL_PRETTY = CALLRAIL_PHONENUMBER.formatNational();

export async function fetcher(...args: Parameters<typeof fetch>) {
  return (await fetch(...args)).json();
}

export const normalizeUrl = (url: string): string => {
  if (!url) return "";

  const hasProtocol = /^(https?:\/\/)/i.test(url);
  if (hasProtocol) return url;

  const isLikelyExternal = /^(www\.|[^/]+\.[^/]+)/i.test(url);
  if (isLikelyExternal) return `https://${url}`;

  const isRelativePath = typeof url === "string" && url.startsWith("/");
  if (isRelativePath) return withUrl(url);

  return withUrl(`/${url}`);
};

export const sleep = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

interface TextNode {
  text: string;
}

interface ContentBlock {
  children?: (string | TextNode)[];
  [key: string]: unknown;
}
export const calculateWordCount = (
  content: (string | ContentBlock)[]
): number => {
  return content.reduce((count: number, contentBlock) => {
    if (typeof contentBlock === "string") {
      // If the content block is a string, split and count words
      return count + contentBlock.trim().split(/\s+/).length;
    } else if (typeof contentBlock === "object" && contentBlock.children) {
      // If the content block has a 'children' property, assume it's an array of text nodes
      return (
        count +
        contentBlock.children.reduce((blockCount: number, child) => {
          if (typeof child === "string") {
            return blockCount + child.trim().split(/\s+/).length;
          } else if (typeof child === "object" && "text" in child) {
            return blockCount + child.text.trim().split(/\s+/).length;
          }
          return blockCount;
        }, 0)
      );
    }
    return count;
  }, 0);
};

// eslint-disable-next-line
export const calculateBuilderWordCount = (blocks: any): number => {
  if (!blocks) return 0;

  // eslint-disable-next-line
  return blocks.reduce((count: number, block: any) => {
    // Handle text components
    if (block.component?.name === "Text" && block.component.options?.text) {
      return count + block.component.options.text.trim().split(/\s+/).length;
    }

    // Recursively handle children blocks
    if (block.children && block.children.length > 0) {
      return count + calculateBuilderWordCount(block.children);
    }

    return count;
  }, 0);
};

/**
 * Calculates estimated reading time in minutes based on word count
 * Assumes average reading speed of 200 words per minute
 * Rounds up to nearest minute
 */
export const calculateTimeToRead = (wordCount: number): number => {
  const WORDS_PER_MINUTE = 238;
  return Math.ceil(wordCount / WORDS_PER_MINUTE);
};

export const BENEFITS_ICON_STYLES = "size-10 lg:size-16";
